import { Heading } from "@/components/ui/heading";
import { CloseIcon, Icon } from "@/components/ui/icon";
import {
  Modal,
  ModalBackdrop,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
} from "@/components/ui/modal";
import { VStack } from "@/components/ui/vstack";
import { Text } from "@/components/ui/text";
import { useInvitationToken } from "@/utils/useInvitationToken";
import {
  Button,
  ButtonIcon,
  ButtonSpinner,
  ButtonText,
} from "@/components/ui/button";
import { Check } from "lucide-react-native";
import {
  getOrganizationControllerListQueryKey,
  getUserPartControllerMyParticipantsQueryKey,
  useOrganizationInviteControllerAcceptInvitation,
  useOrganizationInviteControllerFindOrganization,
} from "@/generated/api/api";
import { useEffect, useState } from "react";
import { Link as ExpoLink, useRouter } from "expo-router";
import { useQueryClient } from "@tanstack/react-query";
import { FetchError } from "@/utils/api.fetch.client";
import { is40x } from "@/utils/error.checker";

function InvitationModal({
  invitationToken,
  onSettle,
}: {
  invitationToken: string;
  onSettle: () => void;
}) {
  const {
    data: organization,
    error,
    isFetched,
  } = useOrganizationInviteControllerFindOrganization(invitationToken, {
    query: {
      meta: {
        noSystemError: true,
      },
      retry(failureCount, error) {
        if (is40x(error)) {
          return false;
        }

        return failureCount < 3;
      },
    },
  });

  const { mutate: acceptInvitation, isPending } =
    useOrganizationInviteControllerAcceptInvitation();

  const [modalIsOpen, setModalIsOpen] = useState(false);
  const queryClient = useQueryClient();
  const router = useRouter();

  useEffect(() => {
    if (isFetched) {
      setModalIsOpen(true);
    }
  }, [isFetched]);

  const onDecline = () => {
    setModalIsOpen(false);
    onSettle();
  };

  const onAccept = () => {
    acceptInvitation(
      {
        token: invitationToken,
      },
      {
        onSuccess: () => {
          queryClient.invalidateQueries({
            queryKey: getUserPartControllerMyParticipantsQueryKey(),
          });
          queryClient.invalidateQueries({
            queryKey: getOrganizationControllerListQueryKey(),
          });
          setModalIsOpen(false);
          onSettle();
          router.replace("/(tabs)/orgs");
        },
      }
    );
  };

  return organization ? (
    <Modal isOpen={modalIsOpen} onClose={onDecline} size="md">
      <ModalBackdrop />
      <ModalContent>
        <ModalHeader>
          <Heading size="md" className="text-typography-950">
            You've been invited
          </Heading>
          <ModalCloseButton>
            <Icon
              as={CloseIcon}
              size="md"
              className="stroke-background-400 group-[:hover]/modal-close-button:stroke-background-700 group-[:active]/modal-close-button:stroke-background-900 group-[:focus-visible]/modal-close-button:stroke-background-900"
            />
          </ModalCloseButton>
        </ModalHeader>
        <ModalBody>
          <VStack className="gap-4">
            <Text>
              <Text bold>{organization?.name}</Text> has invited you to join
              their team. By clicking "Join" you agree to participate in their
              tips-sharing scheme.
            </Text>
            <Text size="sm">
              This team may use a tip-sharing scheme, where tips are distributed
              according to set rules. If applicable, a portion of your tips may
              be shared with others, and you may also receive tips from the team
              based on the organization's policy.
            </Text>
            <Text size="sm">
              Learn more about tips-sharing scheme in our{" "}
              <ExpoLink href="https://gotips.co/client-terms/" target="_blank">
                <Text size="sm" bold>
                  Terms of Use
                </Text>
              </ExpoLink>
            </Text>
          </VStack>
        </ModalBody>
        <ModalFooter>
          <Button variant="outline" action="secondary" onPress={onDecline}>
            <ButtonText>Decline</ButtonText>
          </Button>
          <Button onPress={onAccept}>
            <ButtonIcon as={Check} />
            <ButtonText>Join team</ButtonText>
            {isPending && <ButtonSpinner />}
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  ) : is40x(error) ? (
    <Modal isOpen={modalIsOpen} onClose={onDecline} size="md">
      <ModalBackdrop />
      <ModalContent>
        <ModalHeader>
          <Heading size="md" className="text-typography-950">
            Invitation has expired
          </Heading>
          <ModalCloseButton>
            <Icon
              as={CloseIcon}
              size="md"
              className="stroke-background-400 group-[:hover]/modal-close-button:stroke-background-700 group-[:active]/modal-close-button:stroke-background-900 group-[:focus-visible]/modal-close-button:stroke-background-900"
            />
          </ModalCloseButton>
        </ModalHeader>
        <ModalBody>
          <VStack className="gap-4">
            <Text>
              Unfortunately, your invitation link has expired. Please ask
              team manager to generate new link and send it to you.
            </Text>
            <Text size="sm">
              Feel free to contact our support, if you are facing issues.
            </Text>
          </VStack>
        </ModalBody>
        <ModalFooter>
          <Button onPress={onDecline}>
            <ButtonText>Understood</ButtonText>
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  ) : null;
}

export function InvitationModalHandler(props: {}) {
  const [invitationToken, setInvitationToken] = useInvitationToken();

  if (invitationToken) {
    return (
      <InvitationModal
        invitationToken={invitationToken}
        onSettle={() => setInvitationToken(undefined)}
      />
    );
  }

  return null;
}
